import { getIcon } from '@/utils/iconMapping';
import type { Option } from '@/types/helicopter';
import type { LucideIcon } from 'lucide-react';

// Comprehensive Lucide icon imports for equipment
import {
  Radar, Satellite, Compass, Router, Server, Radio, Antenna, Signal,
  MapPin, Navigation, Map as MapIcon, Globe, Shield, ShieldAlert, ShieldCheck,
  AlertTriangle, Eye, Camera, Video, Lightbulb, Battery, BatteryCharging,
  Zap, Phone, Headphones, Mic, Wifi, Cloud, CloudRain, Plane,
  Cpu, Monitor, HardDrive, Gauge, Crosshair, Target, Lock, Unlock,
  Users, User, Armchair, Sofa, Bed, Waves, Anchor, Droplets, Wrench,
  Star, Mountain, Truck, Package, Boxes, Heart, Activity, Stethoscope,
  Pill, Siren, Thermometer, Snowflake, Sun, Wind, Flame, Filter,
  Volume2, VolumeOff, Moon, Sunrise, Sunset, Focus, Scan,
  Route, MapPinned, Search, Binoculars, Telescope, Microscope, FlaskConical,
  MessageSquare, MessageCircle, Repeat, RotateCw, Scale, Ruler,
  Power, PowerOff, Plug, Cable, CircuitBoard, Microchip, HardDriveDownload,
  Database, Folder, FileText, Clipboard, BookOpen, GraduationCap, Award,
  Flag, Timer, Clock, Calendar, Building, Home, Hospital, Ambulance,
  CarFront, Ship, Cog, Hammer, Drill, Paintbrush, Palette,
  Flashlight, Sparkles, Bolt, Maximize, Minimize, Move, Navigation2,
  Layers, Box, GitBranch, Sliders, ToggleLeft, ToggleRight, Zap as Lightning,
  Workflow, Binary, Code, Terminal, Hash, AtSign, Percent, DollarSign,
  TrendingUp, TrendingDown, BarChart, PieChart, LineChart, ArrowUpCircle,
  ArrowDownCircle, CheckCircle, XCircle, AlertCircle, Info, HelpCircle,
  Settings, Wrench as Spanner, Scissors, Pen, Edit, Save
} from 'lucide-react';

// Category-based alternate pools - Lucide only (expanded with 100+ unique icons)
const categoryPools: Record<string, LucideIcon[]> = {
  avionics: [
    Radar, Satellite, Server, Antenna, Signal, Radio, Cpu, CircuitBoard,
    Microchip, Monitor, Database, HardDrive, Router, Wifi, Terminal,
    Binary, Code, Workflow, Layers, GitBranch, Gauge, Sliders,
    ToggleLeft, ToggleRight, Navigation2, Compass, MapIcon, Globe
  ],
  communication: [
    Phone, Headphones, Mic, MessageSquare, MessageCircle, Router,
    Wifi, Radio, Antenna, Signal, Volume2, VolumeOff, AtSign,
    Hash, MessageSquare, Repeat, RotateCw, Cloud, Server
  ],
  safety: [
    Shield, ShieldAlert, ShieldCheck, AlertTriangle, Lock, Unlock,
    Siren, Heart, Activity, Eye, Focus, CheckCircle, XCircle,
    AlertCircle, Info, HelpCircle, ShieldCheck, Target
  ],
  mission: [
    Target, Crosshair, Search, Binoculars, Camera, Video, Flag,
    Star, Award, Focus, Scan, Telescope, Microscope, Eye,
    Navigation, Route, MapPinned, Maximize, Minimize, Move
  ],
  interior: [
    Armchair, Sofa, Bed, Users, User, Building, Home, Clipboard,
    Folder, FileText, BookOpen, Package, Box, Boxes
  ],
  medical: [
    Ambulance, Hospital, Heart, Activity, Stethoscope, Pill,
    Siren, AlertTriangle, ShieldAlert, FlaskConical, Thermometer,
    Microscope, Clipboard, FileText
  ],
  environmental: [
    Waves, Anchor, Droplets, Cloud, CloudRain, Wind, Snowflake,
    Sun, Thermometer, Moon, Sunrise, Sunset, Mountain, Filter,
    Flame
  ],
  power: [
    Battery, BatteryCharging, Zap, Bolt, Power, PowerOff,
    Plug, Cable, Flame, Lightning, TrendingUp, ArrowUpCircle
  ],
  default: [
    Plane, Cog, Wrench, Package, Boxes, Truck, Spanner,
    Hammer, Drill, Scissors, Pen, Edit, Save, Sliders
  ]
};

// Keyword-specific pools for common collision areas - Lucide only (expanded)
const keywordPools: Record<string, LucideIcon[]> = {
  gps: [
    Satellite, Compass, MapPin, MapPinned, Navigation, MapIcon, Route,
    Globe, Search, Navigation2, Layers, Target
  ],
  transponder: [
    Radar, Antenna, Signal, Target, Satellite, Radio, Router, Server
  ],
  radio: [
    Phone, Radio, Antenna, Signal, Wifi, Router, MessageCircle, Volume2
  ],
  intercom: [
    Headphones, Mic, Phone, MessageSquare, Volume2, Radio, Users
  ],
  seating: [
    Armchair, Sofa, Bed, Users, User, Home
  ],
  police: [
    ShieldAlert, Shield, Siren, AlertTriangle, Lock, Target, Eye
  ],
  ems: [
    Ambulance, Hospital, Heart, Activity, Stethoscope, Pill,
    Siren, AlertTriangle, ShieldCheck
  ],
  camera: [
    Camera, Video, Eye, Focus, Scan, Search, Binoculars, Telescope
  ],
  searchlight: [
    Lightbulb, Flashlight, Sun, Sunrise, Zap, Sparkles, Star
  ],
  floats: [
    Waves, Anchor, Droplets, Ship, Navigation
  ],
  ice: [
    Snowflake, Mountain, Cloud, CloudRain, Wind
  ],
  autopilot: [
    Server, Radar, Cpu, Target, Plane, Navigation, CircuitBoard, Workflow
  ],
  avionics: [
    Radar, Satellite, Monitor, Gauge, CircuitBoard, Cpu, Server, Database
  ],
  navigation: [
    Compass, Navigation, Navigation2, MapIcon, Route, MapPinned, Globe, Satellite
  ],
  weather: [
    Cloud, CloudRain, Wind, Sun, Snowflake, Thermometer, Gauge
  ],
  lighting: [
    Lightbulb, Sun, Moon, Sunrise, Sunset, Flashlight, Sparkles, Star
  ],
  rescue: [
    Ambulance, Heart, ShieldAlert, Siren, Target, Search, Eye
  ]
};

// Global backup pool for ultimate fallback - Lucide only (100+ diverse icons)
const globalPool: LucideIcon[] = [
  Radar, Satellite, Server, Antenna, Signal, Radio, Cpu, CircuitBoard,
  Phone, Headphones, Mic, MessageSquare, Router, Wifi, Shield, ShieldAlert,
  ShieldCheck, AlertTriangle, Lock, Unlock, Siren, Target, Crosshair, Search,
  Camera, Video, Flag, Star, Armchair, Sofa, Users, Ambulance, Hospital,
  Heart, Activity, Stethoscope, Waves, Anchor, Droplets, Battery, Zap,
  Bolt, Power, Plug, Plane, Cog, Wrench, Package, Truck, Monitor,
  Gauge, Navigation, Compass, MapIcon, Globe, Eye, Focus, Scan,
  Lightbulb, Flashlight, Cloud, Wind, Sun, Moon, Thermometer, Snowflake,
  Microchip, Database, HardDrive, Terminal, Code, Workflow, Layers,
  Box, Boxes, Spanner, Hammer, Drill, Sliders, Navigation2, Route,
  MapPinned, Telescope, Binoculars, Microscope, FlaskConical, Clipboard,
  FileText, BookOpen, Building, Home, Ship, Mountain, Filter, Flame,
  Volume2, VolumeOff, MessageCircle, Repeat, RotateCw, Scale, Ruler,
  Calendar, Clock, Timer, Award, GraduationCap, Sparkles, Sunrise,
  Sunset, Maximize, Minimize, Move, GitBranch, ToggleLeft, ToggleRight,
  Binary, Hash, AtSign, CheckCircle, XCircle, AlertCircle, Info,
  HelpCircle, ArrowUpCircle, ArrowDownCircle, TrendingUp, TrendingDown,
  Pen, Edit, Save, Scissors, Spanner, Lightning, Paintbrush, Palette
];

/**
 * Simple hash function for deterministic selection
 */
const hash = (s: string): number => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
  }
  return Math.abs(h);
};

/**
 * Find relevant keyword pools for an option name
 */
const getKeywordPools = (name: string): LucideIcon[][] => {
  const lowerName = name.toLowerCase();
  const pools: LucideIcon[][] = [];
  
  for (const [keyword, pool] of Object.entries(keywordPools)) {
    if (lowerName.includes(keyword)) {
      pools.push(pool);
    }
  }
  
  return pools;
};

/**
 * Pick an alternate icon when the primary is already used
 */
const pickAlternate = (name: string, category: string, used: Set<LucideIcon>): LucideIcon => {
  // Collect all relevant pools
  const pools: LucideIcon[][] = [
    ...getKeywordPools(name),
    categoryPools[category] || categoryPools.default
  ];
  
  // Add global pool at the end
  pools.push(globalPool);
  
  // Try each pool in order
  for (const pool of pools) {
    const start = hash(name) % pool.length;
    for (let i = 0; i < pool.length; i++) {
      const candidate = pool[(start + i) % pool.length];
      if (!used.has(candidate)) {
        return candidate;
      }
    }
  }
  
  // Last resort: return any icon from global pool
  return globalPool[(hash(name) + used.size) % globalPool.length];
};

/**
 * Get a unique icon for an option, avoiding duplicates in the used set
 * NOTE: We bypass getIcon() completely to avoid Settings fallback causing duplicates
 */
export const getUniqueIconForOption = (option: Option, used: Set<LucideIcon>): LucideIcon => {
  // Directly pick from pools based on option name and category
  // This ensures unique, semantically appropriate icons without duplicate Settings icons
  return pickAlternate(option.name, option.category ?? 'default', used);
};

/**
 * Resolve unique icons for a list of options
 * Returns a map of option.id -> unique LucideIcon
 */
export const resolveUniqueIcons = (options: Option[]): Map<string, LucideIcon> => {
  const used = new Set<LucideIcon>();
  const result = new Map() as Map<string, LucideIcon>;
  
  for (const option of options) {
    const Icon = getUniqueIconForOption(option, used);
    used.add(Icon);
    result.set(option.id, Icon);
  }
  
  return result;
};